import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  CalendarDays, Plus, Save, RotateCcw, Trash2, Sun, Moon, Loader2,
  Pencil, X, AlertCircle, Users, Truck as TruckIcon, Building2, ChevronDown, Check,
  CheckCircle, AlertTriangle, XCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

const DAYS = [
  { key: 'lundi', short: 'L' },
  { key: 'mardi', short: 'M' },
  { key: 'mercredi', short: 'Me' },
  { key: 'jeudi', short: 'J' },
  { key: 'vendredi', short: 'V' },
  { key: 'samedi', short: 'S' },
  { key: 'dimanche', short: 'D' },
];

const TruckSlotsConfig = ({ contractId, contract, onUpdate }) => {
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const backupRef = useRef([]);

  // Transport partners + their resources
  const [partners, setPartners] = useState([]);
  const [partnerResources, setPartnerResources] = useState({}); // { partnerId: { drivers: [], vehicles: [] } }

  const billingMode = contract?.billingRules?.billingMode || 'per_unit';
  const isSlotMode = billingMode === 'fleet_location' || billingMode === 'monthly_fixed';
  const fleetTrucks = (contract?.billingRules?.fleetItems || []).reduce((s, fi) => s + (fi.quantity || 0), 0);
  const isOutOfSync = fleetTrucks > 0 && slots.length > 0 && fleetTrucks !== slots.length;

  useEffect(() => {
    const existing = contract?.truckSlots;
    if (existing?.length) setSlots(existing);
    else if (isSlotMode) setSlots(generateFromFleet());
  }, [contract?.truckSlots, contract?.billingRules]);

  // Fetch partners list when editing starts
  const fetchPartners = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transport-partners?limit=200`, {});
      setPartners(res.data.partners || []);
    } catch { /* ignore */ }
  }, []);

  // Fetch specific partner's drivers + vehicles
  const fetchPartnerResources = useCallback(async (partnerId) => {
    if (!partnerId || partnerResources[partnerId]) return;
    try {
      const res = await axios.get(`${API}/transport-partners/${partnerId}`, {});
      const p = res.data;
      setPartnerResources(prev => ({
        ...prev,
        [partnerId]: {
          drivers: p.linked_drivers || p.linkedDrivers || [],
          vehicles: p.linked_vehicles || p.linkedVehicles || [],
        }
      }));
    } catch { /* ignore */ }
  }, [partnerResources]);

  function generateFromFleet() {
    const items = contract?.billingRules?.fleetItems || [];
    const newSlots = [];
    items.forEach((fi) => {
      const qty = fi.quantity || 0;
      const rate = fi.monthlyRatePerTruck || 0;
      const label = fi.vehicleTypeLabel || fi.vehicleType || 'Camion';
      for (let i = 0; i < qty; i++) {
        newSlots.push({
          slotId: `slot-${Date.now()}-${newSlots.length}`,
          label: `${label} #${i + 1}`,
          vehicleType: fi.vehicleType || '',
          monthlyRate: rate,
          daysOfWeek: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
          shift: 'day',
          expectedRunsPerDay: 1,
        });
      }
    });
    return newSlots;
  }

  const startEditing = () => {
    backupRef.current = JSON.parse(JSON.stringify(slots));
    setEditing(true);
    fetchPartners();
    // Pre-fetch resources for already assigned partners
    slots.forEach(s => { if (s.assignedTransporterId) fetchPartnerResources(s.assignedTransporterId); });
  };

  const cancelEditing = () => { setSlots(backupRef.current); setEditing(false); };

  const updateSlot = (idx, field, value) => {
    setSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const toggleDay = (idx, day) => {
    setSlots(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const days = s.daysOfWeek.includes(day) ? s.daysOfWeek.filter(d => d !== day) : [...s.daysOfWeek, day];
      return { ...s, daysOfWeek: days };
    }));
  };

  const handleSelectTransporter = (idx, partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    setSlots(prev => prev.map((s, i) => i === idx ? {
      ...s,
      assignedTransporterId: partnerId,
      assignedTransporterName: partner?.name || partner?.companyName || '',
      assignedDriverIds: [], assignedDriverNames: [],
      assignedVehicleIds: [], assignedVehiclePlates: [],
      assignedDrivers: [], assignedPlates: [],
    } : s));
    if (partnerId) fetchPartnerResources(partnerId);
  };

  const handleToggleDriver = (idx, driver) => {
    setSlots(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const ids = [...(s.assignedDriverIds || [])];
      const names = [...(s.assignedDriverNames || [])];
      const drvIndex = ids.indexOf(driver.id);
      if (drvIndex >= 0) {
        ids.splice(drvIndex, 1);
        names.splice(drvIndex, 1);
      } else {
        ids.push(driver.id);
        names.push(`${driver.firstName || ''} ${driver.lastName || ''}`.trim());
      }
      return { ...s, assignedDriverIds: ids, assignedDriverNames: names, assignedDrivers: names };
    }));
  };

  const handleToggleVehicle = (idx, vehicle) => {
    setSlots(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const ids = [...(s.assignedVehicleIds || [])];
      const plates = [...(s.assignedVehiclePlates || [])];
      const vIdx = ids.indexOf(vehicle.id);
      if (vIdx >= 0) {
        ids.splice(vIdx, 1);
        plates.splice(vIdx, 1);
      } else {
        ids.push(vehicle.id);
        plates.push(vehicle.plateNumber || vehicle.registration || '');
      }
      return { ...s, assignedVehicleIds: ids, assignedVehiclePlates: plates, assignedPlates: plates };
    }));
  };

  const addSlot = () => {
    setSlots(prev => [...prev, {
      slotId: `slot-${Date.now()}`,
      label: `Poste ${prev.length + 1}`,
      vehicleType: prev[0]?.vehicleType || '',
      monthlyRate: prev[0]?.monthlyRate || 0,
      daysOfWeek: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      shift: 'day', expectedRunsPerDay: 1,
    }]);
  };

  const removeSlot = (idx) => setSlots(prev => prev.filter((_, i) => i !== idx));
  const resetFromFleet = () => setSlots(generateFromFleet());

  const totalMonthly = slots.reduce((s, sl) => s + (sl.monthlyRate || 0), 0);
  const totalExpectedPerDay = slots.reduce((s, sl) => s + (sl.expectedRunsPerDay || 0), 0);

  // Conformity check
  const getSlotConformity = (slot) => {
    const hasTransporter = !!slot.assignedTransporterId;
    const hasDrivers = (slot.assignedDriverIds?.length || 0) > 0;
    const hasVehicles = (slot.assignedVehicleIds?.length || 0) > 0;
    if (hasTransporter && hasDrivers && hasVehicles) return 'complete';
    if (hasTransporter || hasDrivers || hasVehicles) return 'partial';
    return 'empty';
  };

  const conformityStats = slots.reduce((acc, s) => {
    const c = getSlotConformity(s);
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, { complete: 0, partial: 0, empty: 0 });

  const globalConformity = conformityStats.empty === slots.length ? 'empty'
    : conformityStats.complete === slots.length ? 'complete' : 'partial';

  const conformityConfig = {
    complete: { label: 'Conforme', icon: CheckCircle, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    partial: { label: 'Incomplet', icon: AlertTriangle, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
    empty: { label: 'Non assigne', icon: XCircle, cls: 'text-gray-500 bg-gray-50 border-gray-200' },
  };

  const saveSlots = async () => {
    // Validation warning
    const incomplete = slots.filter(s => getSlotConformity(s) !== 'complete');
    if (incomplete.length > 0) {
      const proceed = window.confirm(
        `${incomplete.length} poste(s) sans association complete (Transporteur + Chauffeur + Vehicule).\n\nSauvegarder quand meme ?`
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/contracts/${contractId}/truck-slots`, { truckSlots: slots }, {});
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) { console.error('Save slots error:', e); }
    finally { setSaving(false); }
  };

  const ConformityBadge = conformityConfig[globalConformity];

  return (
    <Card data-testid="truck-slots-config">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="w-5 h-5 text-gray-400" />
            Planning des postes
            <Badge variant="outline" className="text-xs ml-1">{slots.length} poste{slots.length > 1 ? 's' : ''}</Badge>
            {slots.length > 0 && (
              <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${ConformityBadge.cls}`} data-testid="conformity-badge">
                <ConformityBadge.icon className="w-3 h-3" />
                {ConformityBadge.label}
                {globalConformity === 'partial' && <span className="text-[10px]">({conformityStats.complete}/{slots.length})</span>}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {!editing ? (
              <Button size="sm" onClick={startEditing} className="gap-1.5 text-xs" data-testid="edit-slots-btn">
                <Pencil className="w-3.5 h-3.5" /> Modifier
              </Button>
            ) : (
              <>
                {isOutOfSync && (
                  <Button size="sm" variant="outline" onClick={resetFromFleet} className="gap-1 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="reset-slots-btn">
                    <RotateCcw className="w-3.5 h-3.5" /> Resynchroniser ({fleetTrucks})
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={addSlot} className="gap-1 text-xs" data-testid="add-slot-btn">
                  <Plus className="w-3.5 h-3.5" /> Ajouter
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEditing} className="gap-1 text-xs" data-testid="cancel-slots-btn">
                  <X className="w-3.5 h-3.5" /> Annuler
                </Button>
                <Button size="sm" onClick={saveSlots} disabled={saving} className="gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-slots-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Sauvegarder
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isOutOfSync && !editing && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="fleet-sync-warning">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>La flotte contractee ({fleetTrucks} camions) ne correspond pas au planning ({slots.length} postes).</span>
          </div>
        )}

        <div className="flex items-center gap-4 text-xs text-gray-500 bg-gray-50 rounded-lg p-2.5">
          <span>Cout mensuel total : <strong className="text-gray-900">{fmt(totalMonthly)} FCFA</strong></span>
          <span className="text-gray-300">|</span>
          <span>Transferts attendus/jour : <strong className="text-gray-900">{totalExpectedPerDay}</strong></span>
        </div>

        {slots.map((slot, idx) => (
          <div key={slot.slotId} className={`border rounded-xl p-4 transition-colors ${editing ? 'border-emerald-200 bg-white' : 'border-gray-100 bg-gray-50/40'}`} data-testid={`slot-row-${idx}`}>
            {editing ? (
              <EditSlotRow
                slot={slot} idx={idx}
                partners={partners}
                resources={partnerResources[slot.assignedTransporterId]}
                onUpdate={updateSlot}
                onToggleDay={toggleDay}
                onSelectTransporter={handleSelectTransporter}
                onToggleDriver={handleToggleDriver}
                onToggleVehicle={handleToggleVehicle}
                onRemove={removeSlot}
                canRemove={slots.length > 1}
              />
            ) : (
              <ReadOnlySlotRow slot={slot} conformity={getSlotConformity(slot)} />
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const EditSlotRow = ({ slot, idx, partners, resources, onUpdate, onToggleDay, onSelectTransporter, onToggleDriver, onToggleVehicle, onRemove, canRemove }) => {
  const [showDrivers, setShowDrivers] = useState(false);
  const [showVehicles, setShowVehicles] = useState(false);
  const drivers = resources?.drivers || [];
  const vehicles = resources?.vehicles || [];

  return (
    <div className="space-y-3">
      {/* Row 1: Label + shift + remove */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Input value={slot.label} onChange={e => onUpdate(idx, 'label', e.target.value)}
            className="h-7 w-36 text-xs font-medium" data-testid={`slot-label-${idx}`} />
          <span className="text-xs text-gray-400">{slot.vehicleType}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => onUpdate(idx, 'shift', slot.shift === 'day' ? 'night' : 'day')}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
              slot.shift === 'day' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
            }`} data-testid={`slot-shift-${idx}`}>
            {slot.shift === 'day' ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
            {slot.shift === 'day' ? 'Jour' : 'Nuit'}
          </button>
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`remove-slot-${idx}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Row 2: Days + runs + rate */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1">
          {DAYS.map(d => (
            <button key={d.key} onClick={() => onToggleDay(idx, d.key)}
              className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${
                slot.daysOfWeek.includes(d.key) ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`} data-testid={`slot-${idx}-day-${d.key}`}>
              {d.short}
            </button>
          ))}
        </div>
        <div className="text-gray-300">|</div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Transferts/jour :</span>
          <Input type="number" min={1} max={10} value={slot.expectedRunsPerDay}
            onChange={e => onUpdate(idx, 'expectedRunsPerDay', Math.max(1, parseInt(e.target.value) || 1))}
            className="h-7 w-14 text-center text-xs" data-testid={`slot-runs-${idx}`} />
        </div>
        <div className="text-gray-300">|</div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Tarif/mois :</span>
          <Input type="number" value={slot.monthlyRate}
            onChange={e => onUpdate(idx, 'monthlyRate', parseInt(e.target.value) || 0)}
            className="h-7 w-28 text-xs" data-testid={`slot-rate-${idx}`} />
          <span className="text-xs text-gray-400">FCFA</span>
        </div>
      </div>

      {/* Row 3: Transporter selector */}
      <div className="border-t border-gray-100 pt-3">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs font-medium text-gray-600">Transporteur</span>
        </div>
        <Select
          value={slot.assignedTransporterId || '_none'}
          onValueChange={(v) => onSelectTransporter(idx, v === '_none' ? null : v)}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`slot-transporter-${idx}`}>
            <SelectValue placeholder="Selectionner un transporteur..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">-- Aucun transporteur --</SelectItem>
            {partners.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.name || p.companyName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Row 4: Driver + Vehicle selectors (only if transporter selected) */}
      {slot.assignedTransporterId && (
        <div className="grid grid-cols-2 gap-3">
          {/* Drivers */}
          <div className="relative">
            <div className="flex items-center gap-2 mb-1.5">
              <Users className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-600">Chauffeur(s)</span>
              {(slot.assignedDriverIds?.length || 0) > 0 && (
                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">{slot.assignedDriverIds.length}</Badge>
              )}
            </div>
            <button
              onClick={() => { setShowDrivers(!showDrivers); setShowVehicles(false); }}
              className="w-full h-8 px-2 text-left text-xs border rounded-md bg-white hover:bg-gray-50 flex items-center justify-between"
              data-testid={`slot-drivers-btn-${idx}`}
            >
              <span className="truncate text-gray-700">
                {(slot.assignedDriverNames?.length) ? slot.assignedDriverNames.join(', ') : 'Selectionner...'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            </button>
            {showDrivers && (
              <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid={`slot-drivers-list-${idx}`}>
                {drivers.length === 0 ? (
                  <p className="text-xs text-gray-400 p-3 text-center">Aucun chauffeur lie</p>
                ) : drivers.map(d => {
                  const name = `${d.firstName || ''} ${d.lastName || ''}`.trim();
                  const selected = (slot.assignedDriverIds || []).includes(d.id);
                  return (
                    <button key={d.id} onClick={() => onToggleDriver(idx, d)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 ${selected ? 'bg-emerald-50' : ''}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300'}`}>
                        {selected && <Check className="w-3 h-3" />}
                      </div>
                      <span className="truncate">{name}</span>
                      {d.status === 'disponible' && <Badge variant="secondary" className="text-[9px] h-3.5 ml-auto">Dispo</Badge>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Vehicles */}
          <div className="relative">
            <div className="flex items-center gap-2 mb-1.5">
              <TruckIcon className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-600">Véhicule(s)</span>
              {(slot.assignedVehicleIds?.length || 0) > 0 && (
                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">{slot.assignedVehicleIds.length}</Badge>
              )}
            </div>
            <button
              onClick={() => { setShowVehicles(!showVehicles); setShowDrivers(false); }}
              className="w-full h-8 px-2 text-left text-xs border rounded-md bg-white hover:bg-gray-50 flex items-center justify-between"
              data-testid={`slot-vehicles-btn-${idx}`}
            >
              <span className="truncate text-gray-700">
                {(slot.assignedVehiclePlates?.length) ? slot.assignedVehiclePlates.join(', ') : 'Selectionner...'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            </button>
            {showVehicles && (
              <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid={`slot-vehicles-list-${idx}`}>
                {vehicles.length === 0 ? (
                  <p className="text-xs text-gray-400 p-3 text-center">Aucun véhicule lie</p>
                ) : vehicles.map(v => {
                  const plate = v.plateNumber || v.registration || 'N/A';
                  const selected = (slot.assignedVehicleIds || []).includes(v.id);
                  return (
                    <button key={v.id} onClick={() => onToggleVehicle(idx, v)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 ${selected ? 'bg-emerald-50' : ''}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300'}`}>
                        {selected && <Check className="w-3 h-3" />}
                      </div>
                      <span className="truncate font-mono">{plate}</span>
                      {v.vehicleType && <span className="text-gray-400 ml-auto text-[10px]">{v.vehicleType}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ReadOnlySlotRow = ({ slot, conformity }) => {
  const transporterName = slot.assignedTransporterName;
  const driverNames = slot.assignedDriverNames?.length ? slot.assignedDriverNames : slot.assignedDrivers || [];
  const plates = slot.assignedVehiclePlates?.length ? slot.assignedVehiclePlates : slot.assignedPlates || [];

  const confIcon = conformity === 'complete' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
    : conformity === 'partial' ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
    : <XCircle className="w-3.5 h-3.5 text-gray-300" />;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {confIcon}
          <span className="text-sm font-medium text-gray-800">{slot.label}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            slot.shift === 'day' ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'
          }`}>
            {slot.shift === 'day' ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
            {slot.shift === 'day' ? 'Jour' : 'Nuit'}
          </span>
          <div className="flex gap-0.5">
            {DAYS.map(d => (
              <span key={d.key} className={`w-6 h-6 rounded text-[10px] flex items-center justify-center ${
                slot.daysOfWeek.includes(d.key) ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-gray-100 text-gray-300'
              }`}>{d.short}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span><strong className="text-gray-800">{slot.expectedRunsPerDay}</strong> transfert{slot.expectedRunsPerDay > 1 ? 's' : ''}/jour</span>
          <span><strong className="text-gray-800">{fmt(slot.monthlyRate)}</strong> FCFA/mois</span>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs pl-6 flex-wrap">
        {transporterName ? (
          <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
            <Building2 className="w-3 h-3" /> {transporterName}
          </span>
        ) : (
          <span className="text-gray-300 italic text-[11px]">Pas de transporteur</span>
        )}
        {driverNames.length > 0 ? (
          <span className="flex items-center gap-1 text-gray-600">
            <Users className="w-3 h-3 text-emerald-500" /> {driverNames.join(', ')}
          </span>
        ) : (
          <span className="text-gray-300 italic text-[11px]">Pas de chauffeur</span>
        )}
        {plates.length > 0 ? (
          <span className="flex items-center gap-1 font-mono text-gray-600">
            <TruckIcon className="w-3 h-3 text-emerald-500" /> {plates.join(', ')}
          </span>
        ) : (
          <span className="text-gray-300 italic text-[11px]">Pas de véhicule</span>
        )}
      </div>
    </div>
  );
};

export default TruckSlotsConfig;
